import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { GlobalComponent } from "../../global-component";
import { AuthUserModel } from 'src/app/account/models/auth.model';

const httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

/** Contexto de cambio de contraseña con token limitado; vive solo en memoria. */
export interface PendingPasswordChange {
    token: string;
    userId: string;
    expiresAt: number;
}

const PASSWORD_CHANGE_TTL_MS = 14 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    // httpDirect bypasses all interceptors — used exclusively for auth endpoints
    private httpDirect: HttpClient;
    private pendingPasswordChange: PendingPasswordChange | null = null;

    constructor(private http: HttpClient, handler: HttpBackend) {
        this.httpDirect = new HttpClient(handler);
    }

    login(email: string, password: string) {
        return this.httpDirect.post(`${GlobalComponent.API_URL}auth/login`, {
            email,
            password
        }, httpOptions).pipe(
            map((response: any) => response),
            catchError(() => throwError(() => new Error('Login failed')))
        );
    }

    refreshToken(): Observable<string> {
        const currentUser = this.getLoggedUser();
        return this.httpDirect.post<any>(`${GlobalComponent.API_URL}auth/refresh`, {}, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentUser?.token ?? ''}`
            })
        }).pipe(
            map((response: any) => {
                if (response?.token) {
                    const updatedUser: AuthUserModel = { ...currentUser!, token: response.token };
                    this.saveUser(updatedUser);
                    return response.token as string;
                }
                throw new Error('No token in refresh response');
            })
        );
    }

    /** Inicia un cambio de contraseña con token de propósito limitado (solo en memoria, 14 min). */
    startPasswordChange(userId: string, token: string): void {
        this.pendingPasswordChange = { token, userId, expiresAt: Date.now() + PASSWORD_CHANGE_TTL_MS };
    }

    getPendingPasswordChange(): PendingPasswordChange | null {
        if (this.pendingPasswordChange && this.pendingPasswordChange.expiresAt <= Date.now()) {
            this.pendingPasswordChange = null;
        }
        return this.pendingPasswordChange;
    }

    clearPendingPasswordChange(): void {
        this.pendingPasswordChange = null;
    }

    /**
     * Cambia la contraseña. Con contexto pendiente usa el token limitado (sin interceptores, para que un
     * 401 no dispare refresh/logout); con sesión activa usa el token completo vía interceptor.
     */
    changePassword(currentPassword: string, newPassword: string): Observable<unknown> {
        const pending = this.getPendingPasswordChange();
        if (pending) {
            return this.httpDirect.post(`${GlobalComponent.API_URL}usuario-sistema/change-password`,
                { currentPassword, password: newPassword, userId: pending.userId },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${pending.token}`
                    })
                });
        }

        const user = this.getLoggedUser();
        if (user?.token) {
            return this.http.post(`${GlobalComponent.API_URL}usuario-sistema/change-password`,
                { currentPassword, password: newPassword, userId: user.userId }, httpOptions);
        }

        return throwError(() => new Error('no-session'));
    }

    saveUser(user: AuthUserModel): void {
        localStorage.setItem('currentUser', JSON.stringify(user));
    }

    getLoggedUser(): AuthUserModel | null {
        const raw = localStorage.getItem('currentUser');
        if (!raw) return null;
        return JSON.parse(raw) as AuthUserModel;
    }

    logout() {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token');
        localStorage.removeItem('menuData');
        localStorage.removeItem('layoutTheme');
    }

    loggedEmployeeHasPermission(permissionCode: string): boolean {
        return this.getLoggedUser()?.permissions.find(x => x === permissionCode) !== undefined;
    }

    canViewOption(permiso: string): boolean {
        return this.getLoggedUser()?.permissions.find(x => x === permiso) !== undefined;
    }
}
